import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Icon } from 'components/v3';
import { embedUrl } from '../../shared/video';
import { phone } from '../palette';
import { Muted } from './styles';

// Content blocks that aren't questions: description, image, video, and the
// section header (title, description, edge-to-edge banner). Images come from
// `assetUrls` (signed URLs for saved assets, object URLs for fresh uploads).

const Heading = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => phone(p.theme).text};
  margin-bottom: ${(p) => p.$gap || 4}px;
  overflow-wrap: anywhere;
`;

// Design: .phone .m-desc.
const Body = styled.div`
  font-size: 15px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${(p) => phone(p.theme).text2};
`;

const Caption = styled(Muted)`
  margin-top: 6px;
  overflow-wrap: anywhere;
`;

const text = (v) => (typeof v === 'string' ? v : '');

export const DescriptionBlock = ({ field }) => {
  const config = field.config || {};
  return (
    <div>
      {text(field.label).trim() ? <Heading>{field.label}</Heading> : null}
      <Body>{text(config.text)}</Body>
    </div>
  );
};
DescriptionBlock.propTypes = { field: PropTypes.object.isRequired };

// Plan §2.3 image sizes.
export const IMAGE_SIZES = {
  small: { width: 150, height: 78 },
  medium: { width: 250, height: 130 },
  large: { width: 380, height: 200 },
};

const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

const ImageRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => JUSTIFY[p.$align] || 'center'};
`;

const Img = styled.img`
  display: block;
  max-width: 100%;
  object-fit: contain;
  border-radius: 10px;
`;

const ImagePlaceholder = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px dashed ${(p) => phone(p.theme).dash};
  background: ${(p) => phone(p.theme).surface};
  color: ${(p) => phone(p.theme).text3};
  font-size: 12px;
`;

export const ImageBlock = ({ field, assetUrls }) => {
  const config = field.config || {};
  const size = IMAGE_SIZES[config.size] || IMAGE_SIZES.medium;
  const url = config.asset_id != null && assetUrls[config.asset_id];
  const caption = text(field.label).trim();
  return (
    <ImageRow $align={config.align}>
      {url ? (
        <Img
          src={url}
          alt={caption}
          style={{ width: size.width, height: size.height }}
        />
      ) : (
        <ImagePlaceholder style={{ width: size.width, height: size.height }}>
          <Icon name="image" size={20} />
          <span>No image uploaded</span>
        </ImagePlaceholder>
      )}
      {caption ? <Caption>{caption}</Caption> : null}
    </ImageRow>
  );
};
ImageBlock.propTypes = {
  field: PropTypes.object.isRequired,
  assetUrls: PropTypes.object.isRequired,
};

const VideoBox = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  background: black;
  border: 1px solid ${(p) => phone(p.theme).border};

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const PlayBadge = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    background: ${(p) => phone(p.theme).accent};
  }
`;

export const VideoBlock = ({ field }) => {
  const config = field.config || {};
  const src = embedUrl(config.url);
  const title = text(field.label).trim();
  const caption = text(config.caption).trim();
  return (
    <div>
      {title ? <Heading $gap={8}>{title}</Heading> : null}
      <VideoBox>
        {src ? (
          <iframe
            src={src}
            title={title || 'Video'}
            allow="fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <PlayBadge role="img" aria-label="Video link not set">
            <span>
              <Icon name="play" size={20} />
            </span>
          </PlayBadge>
        )}
      </VideoBox>
      {caption ? <Caption>{caption}</Caption> : null}
    </div>
  );
};
VideoBlock.propTypes = { field: PropTypes.object.isRequired };

/* ---------- Section header ---------- */

// The banner bleeds past the body's 18px side padding, edge to edge.
const Banner = styled.div`
  margin: ${(p) => (p.$first ? '-18px' : '22px')} -18px 0;
  height: 140px;
  overflow: hidden;
  background: ${(p) => phone(p.theme).surface};
  border-bottom: 1px solid ${(p) => phone(p.theme).border2};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => phone(p.theme).text3};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const SectionTitleBlock = styled.div`
  margin: ${(p) => p.$top}px 0 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${(p) => phone(p.theme).border};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${(p) => phone(p.theme).accent};
  overflow-wrap: anywhere;
`;

const SectionHelp = styled.div`
  font-size: 12px;
  margin-top: 4px;
  white-space: pre-wrap;
  color: ${(p) => phone(p.theme).text3};
`;

const sectionTop = (first, banner) => {
  if (banner) return 14;
  return first ? 4 : 26;
};

export const SectionHeader = ({ section, assetUrls, first }) => {
  const config = section.config || {};
  const banner = config.banner === true;
  const url =
    banner && config.banner_asset_id != null
      ? assetUrls[config.banner_asset_id]
      : null;
  return (
    <>
      {banner ? (
        <Banner $first={first}>
          {url ? <img src={url} alt="" /> : <Icon name="image" size={22} />}
        </Banner>
      ) : null}
      <SectionTitleBlock $top={sectionTop(first, banner)}>
        <SectionTitle>{text(section.label).trim() || 'Section'}</SectionTitle>
        {text(section.help).trim() ? (
          <SectionHelp>{section.help}</SectionHelp>
        ) : null}
      </SectionTitleBlock>
    </>
  );
};
SectionHeader.propTypes = {
  section: PropTypes.object.isRequired,
  assetUrls: PropTypes.object.isRequired,
  first: PropTypes.bool,
};
SectionHeader.defaultProps = { first: false };
